/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Credential model for the AI Model form: what a provider needs, what the
 * operator has entered, and how that becomes a saved partner_connection.
 *
 * Separate from the panel component so the form can import the validator and
 * the connection builder without dragging a component through fast-refresh.
 */
import { useQuery } from '@tanstack/react-query';

import { aiModelsService } from '../../services/ai-models.service';
import { partnerConnectionsService } from '../../services/partner-connections.service';
import { partnersService } from '../../services/partners.service';
import type { AiProviderSpec } from '../../types';
import type { ConnectionType } from '../../types/partners';

export interface CredentialValue {
  /**
   * The connection already bound to this model, when editing one. Empty on
   * create. A non-empty id with no typed secret means "keep what is stored" —
   * the backend never returns a secret, so a blank box cannot mean "clear it".
   */
  connectionId: string;
  /** Non-secret config fields for a new connection, keyed by the spec's field key. */
  config: Record<string, string>;
  /** The secret for a new connection — an API key, or a whole service-account JSON. */
  secret: string;
  /** Operator-visible name for the connection this creates. */
  name: string;
}

export const EMPTY_CREDENTIALS: CredentialValue = {
  connectionId: '',
  config: {},
  secret: '',
  name: '',
};

/** The specs, shared by the panel and the form. Static per release, so cached hard. */
export const useProviderSpecs = () =>
  useQuery({
    queryKey: ['ai-provider-specs'],
    queryFn: () => aiModelsService.getProviderSpecs(),
    staleTime: Infinity,
  });

export const specFor = (specs: AiProviderSpec[] | undefined, providerType?: string) =>
  specs?.find((s) => s.providerType === providerType);

/** Config defaults declared by a spec, as the form's initial values. */
export const defaultsFor = (spec?: AiProviderSpec): Record<string, string> => {
  const out: Record<string, string> = {};
  spec?.config.forEach((f) => {
    if (f.default !== null && f.default !== undefined) out[f.key] = String(f.default);
  });
  return out;
};

/**
 * What is still missing before this credential could work.
 *
 * Mirrors the backend's own check so the operator is told at the field rather
 * than by a 422 after pressing Save. The backend remains the authority — this
 * is not the only thing standing between a bad row and the database.
 */
/** True when this model already has a credential and none was retyped. */
export const isKeepingStored = (value: CredentialValue): boolean =>
  !!value.connectionId && !value.secret.trim();

export const validateCredentials = (
  value: CredentialValue,
  spec?: AiProviderSpec,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!spec) return errors;

  // Editing a model and leaving the fields alone keeps the stored credential.
  // Demanding the key be retyped to save an unrelated field would be the only
  // reason an operator ever pastes a production secret they did not need to.
  if (isKeepingStored(value)) return errors;

  if (!value.name.trim()) errors.name = 'Give this connection a name';

  spec.credentials.forEach((c) => {
    if (c.required && !value.secret.trim()) errors.secret = `${c.label} is required`;
  });

  // A service-account key is a document, and the most likely mistake is pasting
  // an API key into it. Caught here so the message names the cause instead of
  // surfacing a JSON parse error from the adapter.
  if (spec.authMode === 'sa_json' && value.secret.trim()) {
    try {
      const parsed = JSON.parse(value.secret);
      if (!parsed || typeof parsed !== 'object') throw new Error('not an object');
    } catch {
      errors.secret = 'This must be the whole service-account JSON file, not an API key';
    }
  }

  spec.config.forEach((f) => {
    if (f.required && !(value.config[f.key] || '').trim()) {
      errors[`config.${f.key}`] = `${f.label} is required`;
    }
  });

  return errors;
};

/**
 * Create the partner_connection a new credential needs, and return its id.
 *
 * partner_connections are hung off a partner, so one is reused or created per
 * provider — an LLM vendor is exactly the "external system owned by someone
 * else" a partner already models. Doing it here keeps the operator on the AI
 * Models screen instead of sending them to Partners and back with an ObjectId.
 */
export const createConnectionFor = async (
  spec: AiProviderSpec,
  value: CredentialValue,
  connectionTypes: ConnectionType[],
  /** The MODEL's project — the connection must live where the model resolves it. */
  appId?: string,
): Promise<string> => {
  const type = connectionTypes.find((t) => t.code === spec.connectionTypeCode);
  if (!type?._id) {
    throw new Error(
      `No "${spec.connectionTypeCode}" connection type exists yet. Run ` +
      'migrations.seed_ai_connection_types to add the AI provider types.',
    );
  }

  const partners = await partnersService.getAllForDropdown();
  const existing = partners.find(
    (p) => (p.name || '').trim().toLowerCase() === spec.label.trim().toLowerCase(),
  );
  const partnerId = existing?._id
    // partnerType only: the backend's PartnerMasterCreateSchema has no
    // partnerCategory field, so sending one was silently dropped.
    ?? (await partnersService.create({
      name: spec.label,
      partnerType: 'VENDOR',
    }))?.data?._id;

  if (!partnerId) throw new Error(`Could not create a partner record for ${spec.label}.`);

  // Only fields the spec declared, and only ones with a value — an empty string
  // written into connectionConfig would shadow the adapter's own default.
  const connectionConfig: Record<string, string> = {};
  spec.config.forEach((f) => {
    const v = (value.config[f.key] || '').trim();
    if (v) connectionConfig[f.key] = v;
  });

  const payload: any = {
    partnerId,
    name: value.name.trim(),
    connectionType: type._id,
    authType: value.secret.trim() ? 'BEARER' : spec.connectionAuthType,
    connectionConfig,
  };
  // The spec names which slot seals the secret; every AI credential currently
  // uses the auth-token slot, including a service-account JSON.
  if (value.secret.trim()) payload.connectionAuthToken = value.secret.trim();

  const created = await partnerConnectionsService.create(payload, appId || undefined);
  const id = created?.data?._id || created?._id;
  if (!id) throw new Error('The connection was created but returned no id.');
  return id;
};
